import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useState } from 'react'
import { Layers } from 'lucide-react'
import { blockTheme, cn, hsl } from '@teach/ui'
import type { DesignElementType } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { getTypeIconColor } from '@/features/editor/constants/blockTypeColors'
import { useHoveredDesignElement } from '@/features/editor/lib/designHoverPreview'
import { DesignAddElement } from './DesignAddElement'
import { DesignGhostElement } from './DesignGhostElement'

interface Props {
  onAdd: (type: DesignElementType) => void
  contentLang: ContentLang
}

/**
 * Пустой составной блок на холсте (только в режиме правки): по центру карточка-
 * объяснение, внизу «+». Раскрыли «+» или примеряют элемент из панели — карточка
 * прячется, сверху видна примерка, а ряд типов прижат к низу и не сдвигается.
 * Красится из `blockTheme`: канвас светлый, тема приложения тёмная.
 */
export const DesignEmptyState = ({ onAdd, contentLang }: Props) => {
  const m = useMessages(messages)
  const [adding, setAdding] = useState(false)
  const hovered = useHoveredDesignElement()
  const showCard = !adding && !hovered

  return (
    <div className="relative flex h-full w-full items-center justify-center p-6">
      <div className="absolute inset-x-4 top-4">
        <DesignGhostElement contentLang={contentLang} />
      </div>
      {showCard ? (
        // Стеклянная плашка без пунктира (`glass-panel-light`): узор фона урока
        // мягко просвечивает сквозь неё.
        <div className="glass-panel-light flex w-full max-w-[280px] flex-col items-center rounded-3xl px-5 py-6 text-center">
          {/* Иконка типа — тем же цветом и без подложки, что в ленте и в шапке панели. */}
          <Layers className={cn('mb-4 size-10', getTypeIconColor('design'))} strokeWidth={1.75} />
          <p
            className="mb-1.5 text-base font-semibold"
            style={{ color: hsl(blockTheme.foreground) }}
          >
            {m.emptyTitle}
          </p>
          <p
            className="text-sm leading-relaxed"
            style={{ color: hsl(blockTheme.foreground, 0.55) }}
          >
            {m.emptyHint}
          </p>
        </div>
      ) : null}
      <div className="absolute inset-x-4 bottom-4 flex justify-center">
        <DesignAddElement onAdd={onAdd} onOpenChange={setAdding} />
      </div>
    </div>
  )
}
